import * as React from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import { useForm } from 'react-hook-form';
import { useIntercom } from 'react-use-intercom';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import {
  restaurantOnboardingFormDefaultValues,
  activeRestaurantOnboardingStateAtom,
  isManualEntryAtom,
  googlePlacesLastStatusAtom,
  googleSearchNoResultSeenAtom,
} from '../../restaurants/restaurant-onboarding-modal';
import { RestaurantOnboardingPayload } from '../../restaurants/restaurant-onboarding-modal/restaurant-onboarding-form';
import RestaurantOnboarding from '../../restaurants/restaurant-onboarding-modal/restaurant-onboarding';
import {
  activeAddressIdAtom,
  activeRestaurantAtom,
  googleAddresInputValueAtom,
} from '../../restaurants/restaurant-onboarding-modal/google-address-input-result';
import {
  useFormatRestaurantOnboardingData,
  useRestaurantOnboardingFormReset,
  useRestaurantOnboardingResetActions,
} from '../../restaurants/restaurant-onboarding-modal/restaurant-onboarding-modal.hooks';
import { binaryPicturesAtom } from '../../restaurants/restaurant-onboarding-modal/restaurant-onboarding-image-gallery';
import { restaurantInformationDataAtom } from '../../restaurants/restaurant-onboarding-modal/use-restaurant-onboarding-data-query';
import { Button as ButtonV2 } from '@restworld/ui-ds-base';
import {
  orgOnboardingRestaurantDataAtom,
  orgOnboardingBinaryPicturesAtom,
  orgOnboardingSaveAndContinueLaterAtom,
  orgOnboardingReferralInfoAtom,
  orgOnboardingReferralPrefillAtom,
  useOrgOnboardingSteps,
  useOrgSubStepUrlSync,
  FormattedOrgRestaurantData,
} from '../use-organization-onboarding';
import { useOrganizationsService } from '@restworld/data-services';
import { useProspectUpsert } from '../use-prospect-tracking';
import { cn, useAnalytics, useIsVirtualKeyboardOpen } from '@restworld/utils-common';
import { hasValidCoordinates } from '../../shared/manual-venue-entry/address-coordinates';

// Footer component for venue onboarding within org onboarding
const OrgVenueOnboardingFooter = ({
  isInputFilled,
  onContinue,
  isLoading,
  isAddressIncomplete,
}: {
  isInputFilled: boolean;
  onContinue: () => void;
  isLoading: boolean;
  isAddressIncomplete: boolean;
}) => {
  const { t } = useTranslation('jp-onboarding');
  const { t: tC } = useTranslation('common');
  const activeItem = useAtomValue(activeAddressIdAtom);
  const activeUI = useAtomValue(activeRestaurantOnboardingStateAtom);
  const isManualEntry = useAtomValue(isManualEntryAtom);
  const { showMessages: showIntercomMessage } = useIntercom();
  const isKeyboardOpen = useIsVirtualKeyboardOpen();

  const isNoneOfResultsSelected = activeItem === 'none_of_the_results';

  // Determine if continue button should be disabled
  const isContinueDisabled = React.useMemo(
    () =>
      activeUI === 'select_restaurant_from_google'
        ? !activeItem || !isInputFilled
        : (!activeItem && !isManualEntry) || isAddressIncomplete,
    [activeUI, activeItem, isInputFilled, isManualEntry, isAddressIncomplete]
  );

  // Handle continue click
  const handleContinue = React.useCallback(() => {
    if (isNoneOfResultsSelected) {
      showIntercomMessage();
      return;
    }
    onContinue();
  }, [isNoneOfResultsSelected, showIntercomMessage, onContinue]);

  // Don't render footer during AI loading
  if (activeUI === 'magic_ai_loading') return null;
  // Hide footer on search screen when input is empty
  if (activeUI === 'select_restaurant_from_google' && !isInputFilled) return null;

  return (
    // Hidden, not unmounted, while the phone keyboard is open: above it the bar only takes room.
    <div
      className={cn(
        'w-full min-h-[80px] py-3 border-t border-neutral-40 bg-neutral-10 inline-flex justify-center items-center fixed bottom-0 left-0 right-0 z-[100]',
        isKeyboardOpen && 'hidden'
      )}
    >
      <div className="flex justify-evenly items-center gap-3 w-full max-w-[40rem] px-4">
        <ButtonV2
          variant="primary"
          className="w-full"
          onClick={handleContinue}
          loading={isLoading}
          disabled={isContinueDisabled || isLoading}
        >
          {isNoneOfResultsSelected ? tC('contact_support') : t('continue')}
        </ButtonV2>
      </div>
    </div>
  );
};

/**
 * Restaurant creation step for the organization onboarding flow.
 * Reuses the existing venue onboarding UI but instead of calling the create restaurant API,
 * it stores the formatted restaurant data in an atom for later bulk creation.
 */
const RestaurantCreationStep: React.FunctionComponent = () => {
  const { t } = useTranslation('restaurant-onboarding');
  const [activeUI, setActiveUI] = useAtom(activeRestaurantOnboardingStateAtom);
  const googleInputAddressValue = useAtomValue(googleAddresInputValueAtom);
  const setGoogleInputValue = useSetAtom(googleAddresInputValueAtom);
  const activeAddressId = useAtomValue(activeAddressIdAtom);
  const setActiveAddressId = useSetAtom(activeAddressIdAtom);
  const setActiveRestaurant = useSetAtom(activeRestaurantAtom);
  const isInputFilled = React.useMemo(() => googleInputAddressValue?.length > 0, [googleInputAddressValue?.length]);
  const setRestaurantData = useSetAtom(orgOnboardingRestaurantDataAtom);
  const setOrgBinaryPictures = useSetAtom(orgOnboardingBinaryPicturesAtom);
  const existingRestaurantData = useAtomValue(orgOnboardingRestaurantDataAtom);
  const orgBinaryPictures = useAtomValue(orgOnboardingBinaryPicturesAtom);
  const setBinaryPictures = useSetAtom(binaryPicturesAtom);
  const setRestaurantInformationData = useSetAtom(restaurantInformationDataAtom);
  // Read from the SHARED restaurant form atom (populated by image gallery component)
  const restaurantFormBinaryPictures = useAtomValue(binaryPicturesAtom);
  const { goToNextStep, goToStep } = useOrgOnboardingSteps();
  const isSaveAndContinueLater = useAtomValue(orgOnboardingSaveAndContinueLaterAtom);
  const { upsertProspect } = useProspectUpsert();
  const { trackEvent } = useAnalytics();
  const [noResultSeen, setNoResultSeen] = useAtom(googleSearchNoResultSeenAtom);
  const setLastStatus = useSetAtom(googlePlacesLastStatusAtom);
  const referralInfo = useAtomValue(orgOnboardingReferralInfoAtom);
  const [referralPrefill, setReferralPrefill] = useAtom(orgOnboardingReferralPrefillAtom);
  const orgService = useOrganizationsService();
  const isKeyboardOpen = useIsVirtualKeyboardOpen();

  // Whether we have persisted restaurant data to restore (no gmaps_place_id override)
  const hasPersistedRestaurant = !!existingRestaurantData?.restaurants;

  // Sync activeUI to URL as sub_step
  useOrgSubStepUrlSync(activeUI, 'restaurant_creation');

  const methods = useForm<RestaurantOnboardingPayload>({
    defaultValues: restaurantOnboardingFormDefaultValues,
    mode: 'all',
  });

  const { resetState } = useRestaurantOnboardingResetActions();
  const formatFormData = useFormatRestaurantOnboardingData();

  // Manual entry must carry coordinates: without them POST /organizations is
  // rejected with a 422 after the OTP, well past the point where the employer
  // can understand what went wrong. The form fields are the source of truth.
  const isManualEntry = useAtomValue(isManualEntryAtom);
  const addressLat = methods.watch('address_lat');
  const addressLon = methods.watch('address_lon');
  const isAddressIncomplete =
    activeUI === 'restaurant_onboarding_form' && isManualEntry && !hasValidCoordinates(addressLat, addressLon);

  // Read URL params synchronously — always available, unlike router.query which is empty
  // on first render with getStaticProps.
  const { urlGmapsPlaceId, urlSubStep } = React.useMemo(() => {
    if (typeof window === 'undefined') return { urlGmapsPlaceId: null, urlSubStep: null };
    const params = new URLSearchParams(window.location.search);
    return {
      urlGmapsPlaceId: params.get('gmaps_place_id') || null,
      urlSubStep: params.get('sub_step') || null,
    };
  }, []);

  // Skip resetState() when we have a gmaps_place_id URL param, persisted restaurant data,
  // or when the not_horeca / intro sub-step is active — otherwise its 250ms timeout would
  // overwrite activeUI back to 'select_restaurant_from_google' and we'd never see the intro.
  useRestaurantOnboardingFormReset({
    methods,
    // In org onboarding we fully control the step state via URL sync + atoms.
    // The shared restaurant onboarding reset uses a 250ms timer that can race
    // with the org onboarding URL sync and force the UI back to the selection screen.
    // We still clean up on unmount (see effect below).
    skipInitialReset: true,
  });

  // On mount: restore GMaps-selected restaurant to the Google search selection state.
  // Manual entry is handled by the activeUI transition effect below (fires when
  // useOrgOnboardingUrlSync sets activeUI = 'restaurant_onboarding_form').
  const hasRestoredRef = React.useRef(false);
  React.useEffect(() => {
    if (!existingRestaurantData || hasRestoredRef.current || urlGmapsPlaceId) return;
    hasRestoredRef.current = true;

    const { restaurants } = existingRestaurantData;
    const persistedPlaceId = (restaurants as Record<string, unknown>)?.gmaps_place_id as string | undefined;
    if (!persistedPlaceId) return; // manual entry handled by activeUI transition effect

    // Restore Google search selection state so the user can click Continue.
    const displayName = restaurants?.business_address || restaurants?.restaurant_name || '';
    setActiveAddressId(persistedPlaceId);
    setActiveRestaurant(displayName);
    setGoogleInputValue(displayName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  // When activeUI transitions INTO restaurant_onboarding_form — both on initial mount
  // (Effect 1 of useOrgOnboardingUrlSync sets it when restaurantData exists) and when
  // navigating back from the JP step — restore all form-related atoms from the persisted
  // orgOnboardingRestaurantDataAtom (resetState() clears them when continuing forward).
  // Handles BOTH GMaps and manual entry flows.
  const prevActiveUIRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const wasFormView = prevActiveUIRef.current === 'restaurant_onboarding_form';
    prevActiveUIRef.current = activeUI;

    if (activeUI !== 'restaurant_onboarding_form' || wasFormView) return;
    if (!existingRestaurantData?.restaurants) return;

    const { employer_user_contacts, restaurants } = existingRestaurantData;
    const persistedPlaceId = (restaurants as Record<string, unknown>)?.gmaps_place_id as string | undefined;

    const name = restaurants?.restaurant_name?.trim();
    const addr = restaurants?.address?.trim();
    const fallback = [name, addr].filter(Boolean).join(', ');

    // For GMaps flow: use the persisted place_id; for manual entry: use 'restored'
    // Both enable the Continue button in the footer
    setActiveAddressId(persistedPlaceId || 'restored');
    setActiveRestaurant(restaurants?.business_address || fallback);
    setRestaurantInformationData((restaurants || {}) as Parameters<typeof setRestaurantInformationData>[0]);

    if (persistedPlaceId) {
      // GMaps flow: also restore the Google input display value
      const displayName = restaurants?.business_address || restaurants?.restaurant_name || '';
      setGoogleInputValue(displayName);
    }

    if (orgBinaryPictures?.length) {
      setBinaryPictures(orgBinaryPictures);
    }

    const number_of_staffs = String((restaurants?.staff_size_floor ?? 0) + (restaurants?.staff_size_kitchen ?? 0));
    methods.reset({
      ...restaurantOnboardingFormDefaultValues,
      ...employer_user_contacts,
      ...restaurants,
      number_of_staffs,
      business_address: restaurants?.business_address ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUI]);

  // If gmaps_place_id is provided via URL param, skip the Google search step
  // and go straight to the AI loading / data fetch.
  React.useEffect(() => {
    if (urlGmapsPlaceId) {
      setActiveAddressId(urlGmapsPlaceId);
      setActiveUI('magic_ai_loading');
    }
  }, [urlGmapsPlaceId, setActiveAddressId, setActiveUI]);

  // Seed the search box with the venue this prospect named when they asked for the invite.
  //
  // A hint only — we deliberately select nothing for them. Text typed into the Google input
  // without picking a suggestion leaves gmaps_place_id null, and a venue without one never
  // gets the Google enrichment, so the step still has to end in a real selection.
  //
  // Runs once and never over anything already present: a restored venue, a place id from the
  // URL, or whatever the user has started typing all take precedence. `address` is preferred
  // over `name` because it is the formatted string Google itself produced and already opens
  // with the venue name — joining the two would just repeat it.
  const hasSeededFromReferralRef = React.useRef(false);
  React.useEffect(() => {
    if (hasSeededFromReferralRef.current) return;
    if (urlGmapsPlaceId || hasPersistedRestaurant) return;
    if (googleInputAddressValue?.length) return;

    const query = referralPrefill?.venue?.address || referralPrefill?.venue?.name;
    if (!query) return;

    hasSeededFromReferralRef.current = true;
    setGoogleInputValue(query);
  }, [referralPrefill, urlGmapsPlaceId, hasPersistedRestaurant, googleInputAddressValue, setGoogleInputValue]);

  // Ask for the email left on the referral request, but only once a venue has been picked and
  // only for that venue's place id.
  //
  // The referral code on its own is not evidence of who is filling in the form — it arrives
  // in an email anyone can forward — so a match on the chosen venue is what we require before
  // putting an address in front of someone. Fire and forget: the contact step uses whatever
  // landed and asks as usual if nothing did.
  const fetchProspectEmailForSelectedVenue = React.useCallback(() => {
    const referralCode = referralInfo?.referral_code;
    if (!referralCode || referralPrefill?.email) return;
    if (!activeAddressId || activeAddressId === 'restored' || activeAddressId === 'none_of_the_results') return;

    orgService
      .getReferralOnboardingPrefill({ referral_code: referralCode, prospect_gmaps_place_id: activeAddressId })
      .then(({ data }) => {
        if (data?.email) setReferralPrefill((current) => ({ ...(current ?? {}), email: data.email }));
      })
      .catch(() => {
        // Nothing to recover: the contact step asks for the email as it always has.
      });
  }, [referralInfo?.referral_code, referralPrefill?.email, activeAddressId, orgService, setReferralPrefill]);

  const handleContinue = React.useCallback(() => {
    if (activeUI === 'select_restaurant_from_google') {
      trackEvent('employer_onboarding_step_completed', {
        step: 'restaurant_creation',
        sub_step: 'select_restaurant_from_google',
      });
      // Track prospect at the earliest point: user selected a venue and clicked Continue
      upsertProspect({ last_completed_step: 'restaurant_selected' });
      fetchProspectEmailForSelectedVenue();
      setActiveUI('magic_ai_loading');
      return;
    }
    if (activeUI === 'restaurant_onboarding_form') {
      // A disabled button is not a guard: keep the check here too, so a footer
      // regression can't let an uncoordinated address through.
      if (isAddressIncomplete) {
        trackEvent('employer_onboarding_manual_address_blocked', { reason: 'missing_coordinates' });
        return;
      }
      // shouldFocus moves the focus onto the first invalid field, so the employer sees
      // which field blocks them instead of a Continue button that seemingly does nothing.
      methods.trigger(undefined, { shouldFocus: true }).then((isValid) => {
        if (isValid) {
          trackEvent('employer_onboarding_step_completed', {
            step: 'restaurant_creation',
            sub_step: 'restaurant_onboarding_form',
          });
          trackEvent('employer_onboarding_restaurant_created_manually', {
            had_prior_no_result: noResultSeen,
          });
          // Instead of calling the create restaurant API,
          // store the formatted data in the atom for later bulk creation
          const formattedData = formatFormData(methods.getValues());
          setRestaurantData(formattedData as FormattedOrgRestaurantData);

          // Capture binary pictures from shared restaurant form atom to org-specific atom
          // (before resetState() clears the shared atom)
          setOrgBinaryPictures(restaurantFormBinaryPictures);

          // Track prospect with full restaurant + contact data
          // (also triggered reactively via useProspectTracking, but calling here
          // ensures we capture the data even if the user closes the tab right after)
          upsertProspect({ last_completed_step: 'restaurant_creation' });

          // Move to next step in org onboarding.
          // Save-and-continue-later override: if the user clicked that button
          // in the JP header when the venue didn't exist yet, they were
          // rerouted here to create the restaurant first. Now send them
          // straight to user_contact_details because the whole point is to
          // save what they have and exit.
          //
          // Do NOT call resetState() in the save-and-continue branch: it
          // schedules a 250ms-deferred setActiveUI('select_restaurant_from_google')
          // that would clobber the contact step's activeUI and blank it out.
          // The unmount cleanup still clears the shared venue atoms, and the
          // org-create payload already captured the data into org-prefixed
          // atoms above.
          if (isSaveAndContinueLater) {
            goToStep('user_contact_details');
          } else {
            resetState();
            goToNextStep();
          }
        } else {
          // Field names only, never their values: no PII leaves the browser. Read through
          // getFieldState: `methods.formState` is the snapshot of the last render and does
          // not yet hold the errors this trigger just produced.
          const invalidFields = (Object.keys(methods.getValues()) as (keyof RestaurantOnboardingPayload)[]).filter(
            (field) => methods.getFieldState(field).invalid
          );
          trackEvent('employer_onboarding_restaurant_form_invalid', {
            invalid_fields: invalidFields,
            is_manual_entry: isManualEntry,
          });
          // The browser's own scroll-on-focus can leave the field under the fixed footer:
          // center it explicitly.
          const focused = document.activeElement;
          if (focused instanceof HTMLInputElement || focused instanceof HTMLTextAreaElement) {
            focused.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        }
      });
    }
  }, [
    activeUI,
    formatFormData,
    methods,
    setRestaurantData,
    setOrgBinaryPictures,
    restaurantFormBinaryPictures,
    setActiveUI,
    resetState,
    goToNextStep,
    goToStep,
    isSaveAndContinueLater,
    upsertProspect,
    trackEvent,
    noResultSeen,
    isAddressIncomplete,
    isManualEntry,
    fetchProspectEmailForSelectedVenue,
  ]);

  // Clean up on unmount
  React.useEffect(() => {
    return () => {
      resetState();
      setNoResultSeen(false);
      setLastStatus('');
    };
  }, [resetState, setNoResultSeen, setLastStatus]);

  return (
    <div className="pt-[80px] pb-[100px] flex items-center justify-center min-h-screen px-4">
      <RestaurantOnboarding methods={methods} />
      {/* Copyright text - fixed at bottom like footer, only shown on initial empty screen. */}
      {activeUI === 'select_restaurant_from_google' && !isInputFilled && !isKeyboardOpen && (
        <div className="w-full py-3 bg-neutral-10 inline-flex justify-center items-center fixed bottom-0 left-0 right-0 z-[99]">
          <p className="text-center text-sm font-medium text-neutral-400">
            {t('copyright_restworld', {
              year: new Date().getFullYear(),
            })}
          </p>
        </div>
      )}
      <OrgVenueOnboardingFooter
        isInputFilled={isInputFilled}
        onContinue={handleContinue}
        isLoading={false}
        isAddressIncomplete={isAddressIncomplete}
      />
    </div>
  );
};

export default RestaurantCreationStep;
